import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  FaSync,
  FaDownload,
  FaUserMd,
  FaTrash,
  FaToggleOn,
  FaToggleOff,
  FaArrowLeft,
} from "react-icons/fa";
import { buildApiUrl, API_ENDPOINTS } from "../../utils/apiConfig";
import {
  isAppointmentToday,
  formatAppointmentDate,
  getTodayString,
} from "../../utils/dateUtils";
import PatientProfile from "../../components/PatientProfile";
import "./Admin.css";

const TodayAppointments = () => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showPatientProfile, setShowPatientProfile] = useState(false);

  useEffect(() => {
    fetchData();
    // Set up auto-refresh every 10 minutes (reduced frequency)
    const interval = setInterval(fetchData, 600000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        buildApiUrl(API_ENDPOINTS.APPOINTMENTS.DATA)
      );
      const data = await response.json();

      if (data.success) {
        setAppointments(data.appointments || []);
      } else {
        console.error("Failed to fetch appointments:", data.message);
      }
    } catch (error) {
      console.error("Error fetching appointments:", error);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  const updateAppointmentStatus = async (id, newStatus) => {
    try {
      const response = await fetch(
        buildApiUrl(API_ENDPOINTS.APPOINTMENTS.STATUS),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ id, status: newStatus }),
        }
      );

      const data = await response.json();

      if (data.success) {
        setAppointments((prev) =>
          prev.map((apt) =>
            (apt.id || apt.ID) === id
              ? { ...apt, Status: newStatus, status: newStatus }
              : apt
          )
        );
      } else {
        console.error("Failed to update status:", data.message);
      }
    } catch (error) {
      console.error("Error updating appointment status:", error);
    }
  };

  const deleteAppointment = async (timestamp) => {
    if (!window.confirm("Are you sure you want to delete this appointment?")) {
      return;
    }

    try {
      const response = await fetch(
        buildApiUrl(`${API_ENDPOINTS.APPOINTMENTS.DELETE}/${timestamp}`),
        {
          method: "DELETE",
        }
      );

      const data = await response.json();

      if (data.success) {
        setAppointments((prev) =>
          prev.filter(
            (apt) => (apt.id || apt.timestamp || apt.Timestamp) !== timestamp
          )
        );
      } else {
        console.error("Failed to delete appointment:", data.message);
      }
    } catch (error) {
      console.error("Error deleting appointment:", error);
    }
  };

  const handlePatientInfo = async (patientEmail, patientPhone) => {
    try {
      const identifier = patientEmail || patientPhone;
      if (!identifier) {
        console.error("No identifier available for patient");
        setSelectedPatient(null);
        return;
      }

      const response = await fetch(
        buildApiUrl(API_ENDPOINTS.ADMIN.PATIENT_INFO) + "/" + identifier
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.patient) {
        setSelectedPatient(data.patient);
        setShowPatientProfile(true);
      } else {
        console.error("No patient data in response:", data);
        setSelectedPatient(null);
      }
    } catch (error) {
      console.error("Error fetching patient info:", error);
      setSelectedPatient(null);
    }
  };

  const closePatientProfile = () => {
    setShowPatientProfile(false);
    setSelectedPatient(null);
  };

  const handlePatientProfileRefresh = async () => {
    fetchData();

    // If a patient profile is currently open, refresh that patient's data
    if (selectedPatient && showPatientProfile) {
      const identifier = selectedPatient.email || selectedPatient.phone;
      if (identifier) {
        try {
          console.log("🔄 Refreshing current patient data...");
          const response = await fetch(
            buildApiUrl(API_ENDPOINTS.ADMIN.PATIENT_INFO) + "/" + identifier
          );

          if (response.ok) {
            const data = await response.json();
            if (data.success && data.patient) {
              console.log("✅ Patient data refreshed:", data.patient);
              setSelectedPatient(data.patient);
            }
          }
        } catch (error) {
          console.error("❌ Error refreshing patient data:", error);
        }
      }
    }
  };

  // formatAppointmentDate is now imported from dateUtils

  const downloadCSV = async () => {
    try {
      const response = await fetch(buildApiUrl(`/api/appointments/files`));
      const data = await response.json();

      if (data.success) {
        alert(`CSV file location: ${data.filePaths.appointments}`);
      }
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  // Filter appointments for today
  const getTodayAppointments = () => {
    console.log(
      `🔍 TodayAppointments: Filtering ${appointments.length} appointments`
    );
    const todayAppointments = appointments.filter((appointment) => {
      // Handle both old CSV format and new PostgreSQL format
      const appointmentDate =
        appointment["Preferred Date"] || appointment.preferred_date;

      console.log(
        `🔍 Appointment: ${
          appointment.name || appointment.Name
        } - Date: ${appointmentDate}`
      );

      // Use utility function to check if appointment is for today in IST
      return isAppointmentToday(appointmentDate);
    });

    console.log(
      `🔍 TodayAppointments: Found ${todayAppointments.length} appointments for today`
    );
    return todayAppointments;
  };

  const todayAppointments = getTodayAppointments();

  // getTodayString is now imported from dateUtils

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading today's appointments...</p>
      </div>
    );
  }

  return (
    <motion.div
      className="admin-page"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="admin-header">
        <div className="header-content">
          <div className="header-left">
            <button
              className="back-btn"
              onClick={() => navigate("/admin")}
              title="Back to Dashboard"
            >
              <FaArrowLeft /> Back to Dashboard
            </button>
            <h1>📅 Today's Appointments</h1>
            <p className="today-date">{getTodayString()}</p>
          </div>
          <div className="header-right">
            <button
              className="refresh-btn"
              onClick={refreshData}
              disabled={refreshing}
            >
              <FaSync className={refreshing ? "spinning" : ""} />
              {refreshing ? " Refreshing..." : " Refresh"}
            </button>
            <button className="download-btn" onClick={downloadCSV}>
              <FaDownload /> Download CSV
            </button>
          </div>
        </div>
      </div>

      <div className="data-section">
        <div className="section-header">
          <h2>
            Today's Appointments
            <span className="appointment-count">
              ({todayAppointments.length})
            </span>
          </h2>
        </div>

        {todayAppointments.length === 0 ? (
          <div className="no-data">
            <p>No appointments scheduled for today.</p>
            <p className="tip">
              💡 Check other dates or view all appointments.
            </p>
          </div>
        ) : (
          <div className="data-table-container">
            <div className="data-table">
              <table>
                <thead>
                  <tr>
                    <th>Patient Name</th>
                    <th>Contact Info</th>
                    <th>Service</th>
                    <th>Date & Time</th>
                    <th>Additional Info</th>
                    <th>Address & Occupation</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {todayAppointments.map((appointment, index) => (
                    <tr key={index}>
                      <td className="patient-name">
                        <strong>{appointment.name || appointment.Name}</strong>
                        <button
                          className="patient-info-btn"
                          onClick={() =>
                            handlePatientInfo(
                              appointment.email || appointment.Email,
                              appointment.phone || appointment.Phone
                            )
                          }
                          title="View Patient Profile"
                        >
                          <FaUserMd /> Patient Info
                        </button>
                      </td>
                      <td className="contact-info">
                        <div>📧 {appointment.email || appointment.Email}</div>
                        <div>📞 {appointment.phone || appointment.Phone}</div>
                      </td>
                      <td className="service">
                        <span className="service-badge">
                          {appointment.service_required ||
                            appointment["Service Required"]}
                        </span>
                      </td>
                      <td className="datetime">
                        <div className="date">
                          {formatAppointmentDate(
                            appointment["Preferred Date"] ||
                              appointment.preferred_date
                          )}
                        </div>
                        <div className="time">
                          🕐{" "}
                          {appointment["Preferred Time"] ||
                            appointment.preferred_time ||
                            "TBD"}
                        </div>
                      </td>
                      <td className="message-cell">
                        {appointment.additional_info ||
                        appointment["Additional Info"] ? (
                          <span className="message-preview">
                            {(
                              appointment.additional_info ||
                              appointment["Additional Info"]
                            ).substring(0, 50)}
                            {(
                              appointment.additional_info ||
                              appointment["Additional Info"]
                            ).length > 50 && "..."}
                          </span>
                        ) : (
                          <span className="no-info">No additional info</span>
                        )}
                      </td>
                      <td className="address-occupation">
                        <div className="address">
                          {appointment.patient_address ||
                          appointment["Patient Address"] ? (
                            <span className="address-preview">
                              📍{" "}
                              {(
                                appointment.patient_address ||
                                appointment["Patient Address"]
                              ).substring(0, 40)}
                              {(
                                appointment.patient_address ||
                                appointment["Patient Address"]
                              ).length > 40 && "..."}
                            </span>
                          ) : (
                            <span className="no-info">No address</span>
                          )}
                        </div>
                        <div className="occupation">
                          {appointment.patient_occupation ||
                          appointment["Patient Occupation"] ? (
                            <span className="occupation-text">
                              💼{" "}
                              {appointment.patient_occupation ||
                                appointment["Patient Occupation"]}
                            </span>
                          ) : (
                            <span className="no-info">No occupation</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <button
                          className={`status-toggle ${
                            (appointment.status || appointment.Status) ===
                            "Done"
                              ? "done"
                              : "pending"
                          }`}
                          onClick={() =>
                            updateAppointmentStatus(
                              appointment.id || appointment.ID,
                              (appointment.status || appointment.Status) ===
                                "Done"
                                ? ""
                                : "Done"
                            )
                          }
                          title={`Click to change status from ${
                            (appointment.status || appointment.Status) ===
                            "Done"
                              ? "Done"
                              : "Pending"
                          } to ${
                            (appointment.status || appointment.Status) ===
                            "Done"
                              ? "Pending"
                              : "Done"
                          }`}
                        >
                          {(appointment.status || appointment.Status) ===
                          "Done" ? (
                            <FaToggleOn />
                          ) : (
                            <FaToggleOff />
                          )}
                          <span className="status-text">
                            {(appointment.status || appointment.Status) ===
                            "Done"
                              ? "✅"
                              : "⏳"}
                          </span>
                        </button>
                      </td>
                      <td>
                        <button
                          className="delete-btn"
                          onClick={() =>
                            deleteAppointment(
                              appointment.id ||
                                appointment.timestamp ||
                                appointment.Timestamp
                            )
                          }
                          title="Delete this appointment"
                        >
                          <FaTrash />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Patient Profile Modal */}
      {showPatientProfile && (
        <PatientProfile
          patient={selectedPatient}
          onClose={closePatientProfile}
          onRefresh={handlePatientProfileRefresh}
        />
      )}
    </motion.div>
  );
};

export default TodayAppointments;
